<script lang="ts">
	import ButtonGroup from './ButtonGroup.svelte';
	import StreetViewButton from './StreetViewButton.svelte';
	import type {
		AttributesProps,
		AttributesAlertProps,
		AttributesColumnHelper
	} from './Attributes.types.ts';
	import IconZoomTo from '#lib/assets/zoom-to.svg?raw';
	import Alert from './Alert.svelte';
	import InfoButton from './InfoButton.svelte';
	import ReportDataIssueButton from '#lib/issueReporter/ReportDataIssueButton.svelte';

	const {
		icon,
		additionalActions,
		title,
		subheaders = [[]],
		alerts,
		body = [],
		table = [],
		addenda,
		data,
		zoomTo,
		streetView
	}: AttributesProps = $props();

	const filteredAlerts = $derived(
		alerts ? alerts.filter((a): a is AttributesAlertProps => Boolean(a)) : []
	);

	const shouldShow = (col: AttributesColumnHelper) =>
		col.show === undefined || col.show(data[col.accessorKey]);
	const valueOf = (col: AttributesColumnHelper) =>
		col.cell ? col.cell(data[col.accessorKey]) : data[col.accessorKey];
</script>

<div class="attributes">
	<div class="attributesHeader">
		<header class="title">
			<div class="itemsContainer">
				{@render icon?.()}
				<h3 class="overline">{valueOf(title)}</h3>
			</div>
			<div class="actionsContainer">
				{@render additionalActions?.()}
			</div>
		</header>
		<div class="subheader">
			{#each subheaders as subheaderPart, i (i)}
				<div>
					{#each subheaderPart ?? [] as subheader, j (subheader.accessorKey)}
						<span
							>{valueOf(subheader)}{#if j === 0 && subheaderPart?.[1]},&nbsp;{/if}</span>
					{/each}
				</div>
			{/each}
		</div>
	</div>

	<div class="actions">
		<ButtonGroup aria-label="Feature actions">
			{#if zoomTo}
				<button type="button" class="segment" onclick={() => zoomTo()}>
					{@html IconZoomTo}
					<span>Zoom to</span>
				</button>
			{/if}
			{#if streetView}
				<StreetViewButton coords={streetView} />
			{/if}
			<ReportDataIssueButton />
		</ButtonGroup>
	</div>

	{#if filteredAlerts.length > 0}
		<div class="alertContainer">
			{#each filteredAlerts as alert, i (i)}
				<Alert variant={alert.variant} announce={false}>
					<!-- contentHtml is authored static markup; never server/user data. See Attributes.types.ts. -->
					<!-- eslint-disable-next-line svelte/no-at-html-tags -->
					<span>{@html alert.contentHtml}</span>
				</Alert>
			{/each}
		</div>
	{/if}

	<div class="body">
		{#if body.length > 0}
			<dl class="grid">
				{#each body as col (col.accessorKey)}
					{#if shouldShow(col)}
						<div class="gridItem">
							{#if col.header}<dt class="overline">{col.header()}</dt>{/if}
							<dd class="cell">
								{valueOf(col)}
								{#if col.infoText}
									<InfoButton content={col.infoText} />
								{/if}
							</dd>
						</div>
					{/if}
				{/each}
			</dl>
		{/if}

		{#if table.length > 0}
			<dl class="list">
				{#each table as col (col.accessorKey)}
					{#if shouldShow(col)}
						<div>
							<dt>{col.header ? col.header() : ''}</dt>
							<dd>{valueOf(col)}</dd>
						</div>
					{/if}
				{/each}
			</dl>
		{/if}

		{#if addenda && addenda.length > 0}
			<dl class="addenda">
				{#each addenda as col (col.accessorKey)}
					<div>
						<dt>{col.header ? col.header() : ''}:</dt>
						<dd>{valueOf(col)}</dd>
					</div>
				{/each}
			</dl>
		{/if}
	</div>
</div>

<style>
	.attributes {
		background-color: var(--surface-alt);
		padding: var(--space-4) var(--space-4) var(--space-3);
		width: 100%;
	}

	.attributesHeader {
		display: flex;
		flex-direction: column;
		padding-bottom: var(--space-2);
		width: 100%;

		.title {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: var(--space-1);
		}

		.itemsContainer {
			display: flex;
			align-items: center;
			gap: var(--space-3);

			h3 {
				font-size: var(--font-size-base);
			}
		}

		.actionsContainer {
			display: flex;
			align-items: center;
			gap: var(--space-5);
		}

		.subheader {
			display: flex;
			justify-content: space-between;
			padding: var(--space-3) 12% var(--space-2) var(--space-1);
		}
	}

	.actions {
		margin-bottom: var(--space-4);
		container-name: attributes-actions;
		container-type: inline-size;
	}

	.alertContainer {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
		margin-bottom: var(--space-4);
	}

	.body {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
		padding: var(--space-1);
	}

	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
		gap: var(--space-4) var(--space-6);

		dt {
			margin-bottom: var(--space-1);
			font-size: var(--font-size-xs);
		}

		.cell {
			display: flex;
			align-items: center;
			gap: var(--space-3);
			font-size: var(--font-size-xl);

			:global(small) {
				font-size: var(--font-size-sm);
			}

			:global(table) {
				font-size: var(--font-size-base);

				:global(th) {
					text-align: start;
				}
			}
		}
	}

	.list {
		> div {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: var(--space-4);
			padding: var(--space-2) var(--space-3);

			&:nth-child(odd) {
				background-color: var(--surface-sunken);
			}
		}

		dd {
			overflow-wrap: anywhere;
		}
	}

	.addenda {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
		font-size: var(--font-size-sm);

		> div {
			display: flex;
			gap: var(--space-1);
		}
	}
</style>
